import * as React from "react";

import { cn } from "@/lib/utils";
import ErrorAlert from "../error-alert";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
  errorMessage?: string;
}

const FloatTextarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, required, placeholder, id, hasError, errorMessage, ...props },
    ref
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    React.useEffect(() => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = "auto"; // Reset height
        textarea.style.height = `${textarea.scrollHeight + 5}px`; // Set height based on content
      }
    }, [props.value]); // Adjust the height when the value changes

    return (
      <div className={cn("relative", className)}>
        <textarea
          id={id}
          className={cn(
            "flex min-h-[96px]  w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 pt-6 pb-0 text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
            "[&+label]:focus:text-[12px] [&+label]:focus:!text-muted [&+label]:focus:font-light [&+label]:focus:translate-x-0 [&+label]:focus:top-1.5",
            {
              "[&+label]:text-[12px] [&+label]:!text-muted [&+label]:font-light [&+label]:translate-x-0 [&+label]:top-1.5":
                textareaRef.current?.value || props.value,
              "border-error !ring-error": hasError,
            }
          )}
          ref={(node) => {
            textareaRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              (ref as React.RefObject<HTMLTextAreaElement | null>).current =
                node;
            }
          }}
          {...props}
          required={required}
        />
        <label
          htmlFor={id}
          className={cn(
            "transition-all bg-white pl-2 pointer-events-none select-none text-sm text-gray-dark absolute ltr:left-3 rtl:right-3 top-3"
          )}
        >
          {placeholder} {required && <span className="text-red-500">*</span>}
        </label>
        <ErrorAlert title={errorMessage} />
      </div>
    );
  }
);
FloatTextarea.displayName = "Textarea";

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, required, id, hasError, errorMessage, ...props }, ref) => {
    const defaultID = React.useId();

    return (
      <textarea
        id={id ?? defaultID}
        className={cn(
          "flex min-h-[150px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
          className
        )}
        ref={ref}
        {...props}
        required={required}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { FloatTextarea, Textarea };
